import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO({ title: "Admin Login | CT BLAZERS", description: "CT BLAZERS staff admin login.", path: "/admin/login" });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin");
    else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ink-900 relative overflow-hidden px-5">
      <div className="absolute inset-0 court-glow" />
      <div className="absolute inset-0 diag-stripe opacity-20" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <img src={ASSETS.logo} alt="CT Blazers logo" className="h-14 w-auto" />
          <span className="font-heading text-3xl tracking-wide text-white">CT BLAZERS</span>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-ink-700/80 p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-blaze-orange">
            <Lock size={18} />
            <h1 className="font-heading text-2xl tracking-wide">Admin Login</h1>
          </div>
          <p className="mt-1 text-sm text-white/50">Sign in to view website inquiries and testimonials.</p>

          <form onSubmit={submit} data-testid="admin-login-form" className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-white/80 text-xs uppercase tracking-wider">Email</Label>
              <Input
                required
                type="email"
                data-testid="admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-ink-900/60 border-white/15 text-white h-11"
                placeholder="admin@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80 text-xs uppercase tracking-wider">Password</Label>
              <Input
                required
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-ink-900/60 border-white/15 text-white h-11"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12"
            >
              {loading ? <Loader2 className="animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
        <Link to="/" className="mt-6 block text-center text-sm text-white/40 hover:text-white/70">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
